import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ModalWrapper from "../../../app/common/modals/ModalWrapper";
import useFirestoreDoc from "../../../app/hooks/useFirestoreDoc";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { listenToDocFromFirestore } from "../../../app/firestore/firestoreService";
import { Button, Checkbox, Grid } from "semantic-ui-react";
import Statistic from "../../../app/common/components/Statistic";
import Divider from "../../../app/common/components/Divider";
import { CopyToClipboard } from "react-copy-to-clipboard/lib/Component";
import {
  FIRESTORE_JOBS_COLLECTION,
  FIRESTORE_USERS_COLLECTION,
  FIRESTORE_VIEWS_COLLECTION,
  QUERY_CODES,
} from "../../../app/firestore/firestoreConstants";
import { PAYMENT_JOB_POST_SERVICE } from "../../payments/paymentConstants";
import useCustomerDetails from "../../../app/hooks/useCustomerDetails";
import { fetchCustomerDetails } from "../../employer/employerActions";
import { closeService } from "../../../app/api/adminAPI";

export default function AdminJobCloseModal({ job }) {
  const dispatch = useDispatch();

  const { loading, error } = useSelector((state) => state.async);

  const [employerDetails, setEmployerDetails] = useState(null);
  const [isConfirmCloseJob, setIsConfirmCloseJob] = useState(false);
  const [closeError, setCloseError] = useState();
  const [isClosing, setIsClosing] = useState(false);
  const [viewCountForJob, setViewCountForJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [reviewStatus, setReviewStatus] = useState([]);

  useFirestoreDoc({
    shouldExecute: job.refNumber,
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_VIEWS_COLLECTION,
        id: job.refNumber,
      }),
    data: (viewCount) => setViewCountForJob(viewCount),
    deps: [job.refNumber, dispatch],
    queryCode: QUERY_CODES.listenToJobViewCountFromFirestore,
  });

  useFirestoreDoc({
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_JOBS_COLLECTION,
        id: job.id,
      }),
    data: (selectedJob) => setSelectedJob(selectedJob),
    deps: [job.id, dispatch],
    queryCode: QUERY_CODES.listenToJobFromFirestore,
  });

  useFirestoreDoc({
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_USERS_COLLECTION,
        id: job.employerId,
      }),
    data: (details) => setEmployerDetails(details),
    deps: [job.employerId],
    queryCode: QUERY_CODES.getUserAccount,
  });

  useCustomerDetails({
    shouldExecute: employerDetails?.stripeCustomerId,
    account: employerDetails,
    deps: [employerDetails],
  });

  if (
    (loading || !selectedJob || !viewCountForJob || !employerDetails) &&
    !error
  )
    return <LoadingComponent content="Loading job dashboard" />;

  const isSubscription = true;
  const viewCount = viewCountForJob?.viewsCount;

  function handleConfirmStatementToggle(e, { checked }) {
    setIsConfirmCloseJob(checked);
  }

  async function handleCloseJob() {
    try {
      setIsClosing(true);
      const response = await closeService({
        service: PAYMENT_JOB_POST_SERVICE,
        id: selectedJob.id,
        viewCount,
      });
      setReviewStatus(response.body);
    } catch (e) {
      console.log(e);
      setCloseError(e.message);
    } finally {
      setIsClosing(false);
    }
  }

  return (
    <ModalWrapper
      style={{ width: 700 }}
      modalClassName="modal-ux-wrapper"
      closeOnEscape={false}
      closeOnDimmerClick={false}
      padding={30}
      onUnmountAction={() =>
        dispatch(fetchCustomerDetails({ subscription: null, discount: null }))
      }
    >
      <h2>
        Close job <span className="body-blue-text">(Id: {selectedJob.id})</span>
      </h2>
      <Divider dividerLine="2px solid #7FC4FD" margin={"10px 0"} />
      {!loading && employerDetails && (
        <div className="body-blue-text" style={{ marginBottom: 25 }}>
          <div>
            <span className="body-bold-text">Employer details: </span>
            <span>{employerDetails.displayName} / </span>
            <CopyToClipboard text={employerDetails.email}>
              <span className="clickable">{employerDetails.email} / </span>
            </CopyToClipboard>
            <span>{employerDetails.sibId}</span>
          </div>
          <div style={{ marginTop: 8 }}>
            <span className="body-bold-text">REF: </span>
            <CopyToClipboard text={job.refNumber}>
              <span className="clickable">{job.refNumber}</span>
            </CopyToClipboard>
          </div>
          <div style={{ marginTop: 4 }}>
            <span className="body-bold-text">Paid: </span>
            <span>{selectedJob?.isPaidFor ? "Yes" : "No"} </span>
          </div>
          <div style={{ marginTop: 4 }}>
            <span className="body-bold-text">Posted on subscription: </span>
            <span>{isSubscription ? "Yes" : "No"}</span>
          </div>
        </div>
      )}
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <span className="body-bold-text">Date job expired: </span>
            <span>{job.dateExpiring.toString()}</span>{" "}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={6} textAlign="center">
            <Statistic
              heading="Unique Views"
              stat={viewCountForJob.viewsCount}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Divider margin="0 0 15px" />
          <Grid.Column width={9}>{reviewStatus.join(" | ")}</Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ paddingTop: 14 }} columns={2}>
          <Grid.Column with={8} verticalAlign="middle">
            <div>
              <Checkbox
                style={{ marginRight: 10 }}
                onChange={(e, { checked }) =>
                  handleConfirmStatementToggle(e, { checked })
                }
              />
              <span className="main-text">Confirm to close job</span>
            </div>
          </Grid.Column>
          <Grid.Column width={4} floated="right">
            <Button
              className="overwrite-button cta-orange"
              content={"Close job"}
              onClick={handleCloseJob}
              disabled={
                !isConfirmCloseJob ||
                isClosing ||
                selectedJob.isClosed ||
                loading
              }
              loading={isClosing}
            />
            {(error || closeError) && (
              <div className="error-message">{error || closeError}</div>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {selectedJob.isClosed && (
        <div className="semi-bold">Job closed successfully!</div>
      )}
    </ModalWrapper>
  );
}
